import styled from '@emotion/styled';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { colors as dsColors } from '@modernhealth/vital-foundation';
import {
    BodyTextSmall,
    BodyTextXSmall,
    HeadingText20,
    HeadingText30,
    pxToRem,
} from '@modernhealth/vital-components';
import { color } from 'src/shared/styles/variables';

interface Props {
    lastRating?: number;
    ratingId: string;
    title: string;
    subtitle: string;
    onSelect: (ratingId: string, value: number) => void;
}

const OuterDiv = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    marginBottom: pxToRem(24),
});

interface BlueBarProps {
    width: string;
}

const BlueBar = styled.div<BlueBarProps>(({ width }) => ({
    position: 'absolute',
    width,
    height: '110%',
    backgroundColor: dsColors.blue55,
    top: pxToRem(-2),
    left: pxToRem(-2.5),
    borderRadius: pxToRem(24),
}));

const RatingButtonsDiv = styled('div')({
    position: 'relative',
    display: 'flex',
    border: `2px solid ${color.catskillWhite}`,
    borderRadius: pxToRem(24),
    padding: `${pxToRem(3)} 0`,
    backgroundColor: color.coconut,
    justifyContent: 'space-around',
});

interface RatingButtonProps {
    selected: boolean;
}

const RatingButton = styled('button')<RatingButtonProps>(({ selected }) => ({
    border: 'none',
    boxShadow: 'none',
    textAlign: 'center',
    '&:hover': {
        border: 'none',
        boxShadow: 'none',
    },
    '&:focus': {
        border: 'none',
        boxShadow: 'none',
    },
    height: pxToRem(36),
    width: pxToRem(36),
    borderRadius: '100%',
    color: dsColors.gray85,
    backgroundColor: selected ? color.white : 'transparent',
    zIndex: 10,
}));

const RatingTextValue = styled(HeadingText20)({
    display: 'inline-block',
    textAlign: 'center',
});

const RatingDescriptors = styled('div')({
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: pxToRem(16),
    marginBottom: pxToRem(20),
});

export const Rating1to5: React.FC<Props> = ({
    ratingId,
    lastRating,
    title,
    subtitle,
    onSelect,
}) => {
    const [rating, setRating] = useState(lastRating || 0);
    const { t } = useTranslation();

    const handleRating = (value: number) => evt => {
        evt.preventDefault();
        evt.stopPropagation();
        setRating(value);
        onSelect(ratingId, value);
    };

    const blueBarWidthForEachRating = {
        0: '0%',
        1: '15.5%',
        2: '35.5%',
        3: '55.5%',
        4: '75.5%',
        5: '101%',
    };

    return (
        <OuterDiv>
            <HeadingText30 fontColor="gray85">{t(title)}</HeadingText30>
            <BodyTextSmall fontColor="gray65" marginBottom={20} marginTop={5}>
                {t(subtitle)}
            </BodyTextSmall>
            <RatingButtonsDiv>
                <BlueBar width={blueBarWidthForEachRating[rating]} />

                {[...Array(5)].map((_, idx) => {
                    const value = idx + 1;

                    return (
                        <RatingButton
                            value={value}
                            key={`${ratingId}_${value}`}
                            onClick={handleRating(value)}
                            selected={rating === value}
                        >
                            <RatingTextValue
                                fontColor={!!rating && value < rating ? 'white' : 'gray65'}
                            >
                                {value}
                            </RatingTextValue>
                        </RatingButton>
                    );
                })}
            </RatingButtonsDiv>
            <RatingDescriptors>
                <BodyTextXSmall fontColor="gray65">
                    {t('intakeForms:numerical_rating.least_important')}
                </BodyTextXSmall>
                <BodyTextXSmall fontColor="gray65">
                    {t('intakeForms:numerical_rating.most_important')}
                </BodyTextXSmall>
            </RatingDescriptors>
        </OuterDiv>
    );
};
